import { useCallback, useMemo } from 'react';
import { addDays, startOfDay } from 'date-fns';
import type {
  CashList,
  CashListTransaction,
  CashListTransactionFilters,
} from '@pflegenavi/frontend/api-nursing-home';
import {
  useCashListConfiguration,
  useCashManagementPhoenixApi,
} from '@pflegenavi/frontend/api-nursing-home';

export const cashListAtDate = (
  cashList: CashList,
  lastTransaction:
    | Pick<
        CashListTransaction,
        'balance_after' | 'coins_after' | 'bank_account_amount_after'
      >
    | undefined
): CashList => {
  if (!lastTransaction) {
    return {
      ...cashList,
      totalInCent: 0,
      coins: cashList.coins.map((coin) => ({ ...coin, amount: 0 })),
      bankAccountAmountInCent: 0,
    };
  }
  return {
    ...cashList,
    totalInCent: lastTransaction.balance_after,
    coins: lastTransaction.coins_after,
    bankAccountAmountInCent: lastTransaction.bank_account_amount_after,
  };
};

// The running balance is ordered by creation datetime, so the cut-off is too.
export const endOfDayFilter = (date: Date): CashListTransactionFilters => ({
  createdTo: startOfDay(addDays(date, 1)).toISOString(),
});

export const useInstitutionCashListAtDate = (): {
  institutionCashList: CashList | undefined;
  fetchInstitutionCashListAtDate: (date: Date) => Promise<CashList>;
} => {
  const { cashLists, nursingHomeCashCashListId } = useCashListConfiguration();
  const api = useCashManagementPhoenixApi();

  const institutionCashList = useMemo(
    () =>
      cashLists.find((cashList) => cashList.id === nursingHomeCashCashListId),
    [cashLists, nursingHomeCashCashListId]
  );

  const fetchInstitutionCashListAtDate = useCallback(
    async (date: Date) => {
      if (!api || !institutionCashList) {
        throw new Error('Institution cash list is not available');
      }
      const response = await api.getCashListTransactions({
        params: {
          cashListId: institutionCashList.id,
          page: 1,
          pageSize: 1,
          orderBy: 'creation_datetime',
          orderDirection: 'desc',
          filters: endOfDayFilter(date),
        },
      });
      return cashListAtDate(institutionCashList, response.data[0]);
    },
    [api, institutionCashList]
  );

  return { institutionCashList, fetchInstitutionCashListAtDate };
};
